import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contactsApi, ApiError, type Contact } from '../lib/api';

export default function ContactManagement() {
  const queryClient = useQueryClient();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState<Contact | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    affiliation: '',
    email: '',
    phone: '',
    notes: '',
  });
  const [error, setError] = useState('');

  const { data: contacts, isLoading } = useQuery({
    queryKey: ['contacts'],
    queryFn: contactsApi.list,
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => contactsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setShowAddModal(false);
      resetForm();
    },
    onError: (err) => {
      if (err instanceof ApiError) setError(err.message);
      else setError('作成に失敗しました');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: { id: string } & typeof formData) =>
      contactsApi.update(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setShowEditModal(null);
      resetForm();
    },
    onError: (err) => {
      if (err instanceof ApiError) setError(err.message);
      else setError('更新に失敗しました');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => contactsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });

  const resetForm = () => {
    setFormData({ name: '', role: '', affiliation: '', email: '', phone: '', notes: '' });
    setError('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (showEditModal) {
      updateMutation.mutate({ id: showEditModal.id, ...formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const openEditModal = (contact: Contact) => {
    setFormData({
      name: contact.name,
      role: contact.role || '',
      affiliation: contact.affiliation || '',
      email: contact.email || '',
      phone: contact.phone || '',
      notes: contact.notes || '',
    });
    setShowEditModal(contact);
  };

  const handleDelete = (contact: Contact) => {
    if (confirm(`${contact.name}を削除しますか？`)) {
      deleteMutation.mutate(contact.id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-theme-primary">知人リスト</h1>
        <button
          onClick={() => { resetForm(); setShowAddModal(true); }}
          className="px-4 py-2 bg-accent text-navy-900 font-bold rounded-lg hover:opacity-90 transition-opacity"
        >
          + 知人を追加
        </button>
      </div>

      <div className="bg-theme-card rounded-xl border border-theme overflow-hidden transition-colors duration-300">
        {isLoading ? (
          <div className="p-8 text-center text-theme-secondary">読み込み中...</div>
        ) : contacts && contacts.length > 0 ? (
          <table className="w-full">
            <thead className="bg-theme-hover">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-theme-secondary">名前</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-theme-secondary">役職</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-theme-secondary">所属</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-theme-secondary">連絡先</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-theme-secondary"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-theme">
              {contacts.map(contact => (
                <tr key={contact.id} className="hover:bg-theme-hover transition-colors">
                  <td className="px-4 py-4 text-theme-primary font-medium">{contact.name}</td>
                  <td className="px-4 py-4 text-theme-secondary">{contact.role || '-'}</td>
                  <td className="px-4 py-4 text-theme-secondary">{contact.affiliation || '-'}</td>
                  <td className="px-4 py-4 text-theme-secondary">
                    {contact.email && <div>{contact.email}</div>}
                    {contact.phone && <div>{contact.phone}</div>}
                    {!contact.email && !contact.phone && '-'}
                  </td>
                  <td className="px-4 py-4">
                    <button
                      onClick={() => openEditModal(contact)}
                      className="text-accent hover:underline text-sm mr-4"
                    >
                      編集
                    </button>
                    <button
                      onClick={() => handleDelete(contact)}
                      className="text-red-500 hover:text-red-400 text-sm"
                    >
                      削除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-8 text-center text-theme-secondary">
            知人がいません
          </div>
        )}
      </div>

      {/* 追加/編集モーダル */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-theme-card rounded-xl p-6 w-full max-w-md border border-theme transition-colors duration-300">
            <h2 className="text-xl font-bold text-theme-primary mb-4">
              {showEditModal ? '知人を編集' : '知人を追加'}
            </h2>
            
            {error && (
              <div className="mb-4 p-3 bg-red-500 bg-opacity-20 border border-red-500 rounded-lg text-red-500 text-sm">
                {error}
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-theme-secondary mb-1">
                  名前 *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-theme-input border border-theme rounded-lg text-theme-primary placeholder-theme-muted focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-theme-secondary mb-1">
                  役職
                </label>
                <input
                  type="text"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-3 bg-theme-input border border-theme rounded-lg text-theme-primary placeholder-theme-muted focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-theme-secondary mb-1">
                  所属
                </label>
                <input
                  type="text"
                  value={formData.affiliation}
                  onChange={(e) => setFormData({ ...formData, affiliation: e.target.value })}
                  className="w-full px-4 py-3 bg-theme-input border border-theme rounded-lg text-theme-primary placeholder-theme-muted focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-theme-secondary mb-1">
                  メール
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-theme-input border border-theme rounded-lg text-theme-primary placeholder-theme-muted focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-theme-secondary mb-1">
                  電話番号
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 bg-theme-input border border-theme rounded-lg text-theme-primary placeholder-theme-muted focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-theme-secondary mb-1">
                  備考
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 bg-theme-input border border-theme rounded-lg text-theme-primary placeholder-theme-muted focus:outline-none focus:ring-2 focus:ring-accent resize-none"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setShowAddModal(false); setShowEditModal(null); resetForm(); }}
                  className="flex-1 py-3 border border-theme text-theme-secondary rounded-lg hover:bg-theme-hover transition-colors"
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="flex-1 py-3 bg-accent text-navy-900 font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending ? '保存中...' : '保存'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
